import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { setOriginalWorkflowVariables, setWorkflowCustomVariables } from "../../../../actions/taskSidebar";
import { notifyIcon } from "../../../../config";
import {
  addAdminCustomVariableDimension,
  addAdminCustomVariableOption,
  deleteAdminCustomVariableDimension,
  deleteAdminCustomVariableOption,
  deleteAllAdminCustomVariableOptions,
  getAdminCustomVariableDimensionAndOptions,
  updateAdminCustomVariableDimension,
  updateAdminCustomVariableOption,
} from "../../../Admin/admin.service";
import { useTeamChipPersistence } from "../../../../shared/components/ChipEditor/TeamChipPersistenceContext";
import {
  addProjectVariableDimension,
  addProjectVariableOption,
  deleteAllProjectVariableOptions,
  deleteProjectVariableDimension,
  deleteProjectVariableOption,
  getProjectVariableDimensionAndOptions,
  updateProjectVariableDimension,
  updateProjectVariableOption,
} from "../../../../shared/services/projectVariables.service";
import { getNotification } from "../../../../utils/common";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { filterMultiDimensionOptions } from "../columnFilter/multiDimensionColumnFilterEval";
import { useVariablesContext } from "../context/variableContext";
import {
  addWorkflowVariableDimension,
  addWorkflowVariableOption,
  deleteAllOptions,
  deleteWorkflowVariableDimension,
  deleteWorkflowVariableOption,
  getDimensionAndOptions,
  getWorkflowVariables,
  updateWorkflowVariableDimension,
  updateWorkflowVariableOption,
} from "../services/workflowVariables.service";
import { coerceCellValueForApi, getMultiDimensionMatrixFromVariable } from "../utils/multiDimensionMatrixFromApi";
import {
  MULTI_DIMENSION_DEFAULT_TYPE,
  MULTI_DIMENSION_MAX_DIMENSION_COLUMNS,
  MULTI_DIMENSION_MIN_DIMENSION_COLUMNS,
  MULTI_DIMENSION_OPTIONS_COLUMN_FILTER_ID,
  VARIABLE_ICONS,
  WORKFLOW_VARIABLE_LABELS,
  normalizeMultiDimensionType,
} from "../workflowVariables.constants";

const NAME_MAX = WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_COLUMN_NAME_MAX;

/**
 * State, derived values, and handlers for the multi-dimension chip properties modal (dimensions × options matrix).
 * @returns {Object} Fields consumed by `MultiDimensionChipPropertiesModal` (grid state, popups, drag-and-drop, API sync).
 */
export const useMultiDimensionChipPropertiesModal = () => {
  const dispatch = useDispatch();
  const { projectId: routeProjectId } = useParams();
  const teamChipPersist = useTeamChipPersistence();
  const { selectedVariable, workflow, setSelectedVariable, setCustomWorkflowVariables } = useWorkflowContext();
  const {
    showMultiDimensionChipModal,
    setShowMultiDimensionChipModal,
    skipDimensionOptionsFetchOnModalOpenRef,
    adminMultiDimensionModalCloseCallbackRef,
  } = useVariablesContext();

  /** Project id for multi-dimension APIs: team ChipEditor context, or route when editing a merged project chip in a form/workflow screen */
  const effectiveProjectIdForMultiDim = teamChipPersist?.projectId ?? (selectedVariable?.isProjectVariable ? routeProjectId : null);
  const persistWorkflowMultiDimensionApis = Boolean(workflow?.Id && selectedVariable?.id && !selectedVariable?.isProjectVariable);
  const teamChipBlocksProjectMultiDim =
    Boolean(teamChipPersist?.projectId) &&
    (Boolean(teamChipPersist?.readOnly) || Boolean(teamChipPersist?.lockPresetFields));
  const persistProjectMultiDimensionApis = Boolean(
    selectedVariable?.id && effectiveProjectIdForMultiDim && !teamChipBlocksProjectMultiDim
  );
  /** Org Profile Control global dropdown chips: add dimension / add option APIs (see admin.service). */
  const persistAdminGlobalMultiDimensionApis = Boolean(
    selectedVariable?.id &&
      selectedVariable?.companyId &&
      selectedVariable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD &&
      !persistWorkflowMultiDimensionApis &&
      !persistProjectMultiDimensionApis
  );
  const persistMultiDimensionApis =
    persistWorkflowMultiDimensionApis || persistProjectMultiDimensionApis || persistAdminGlobalMultiDimensionApis;
  const [dimensions, setDimensions] = useState([]);
  const [options, setOptions] = useState([]);
  const [draggedOptionId, setDraggedOptionId] = useState(null);
  const [lastAddedRowId, setLastAddedRowId] = useState(null);
  const [columnPopup, setColumnPopup] = useState(null);
  const columnPopupAnchorRef = useRef(null);
  const [dimensionFilterPopup, setDimensionFilterPopup] = useState(null);
  const dimensionFilterPopupAnchorRef = useRef(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [popupName, setPopupName] = useState("");
  const [popupType, setPopupType] = useState(MULTI_DIMENSION_DEFAULT_TYPE);
  const [showAddRowClearsFiltersDialog, setShowAddRowClearsFiltersDialog] = useState(false);
  const [isAddingOptionRow, setIsAddingOptionRow] = useState(false);
  const optionAddInFlightRef = useRef(false);
  const optionPersistSnapshotRef = useRef({});

  const optionsRef = useRef(options);
  const dimensionsRef = useRef(dimensions);
  optionsRef.current = options;
  dimensionsRef.current = dimensions;

  const hasActiveColumnFilters = Object.keys(columnFilters).length > 0;

  /**
   * Whether an option row id refers to a persisted server row (not a local draft `opt_*` id).
   * @param {string|number} id Option row id.
   * @returns {boolean}
   */
  const isServerOptionRow = (id) => !(typeof id === "string" && id.startsWith("opt_"));

  /** Builds update-option API payload*/
  function buildOptionApiPayload(opt, dimsOverride = null) {
    const dims = dimsOverride ?? dimensionsRef.current;
    const num = Number(opt.id);
    const value = Number.isFinite(num) ? num : opt.id;
    return {
      value,
      label: String(opt.label ?? "").trim(),
      cells: dims.map((d) => ({
        dimensionId: d.id,
        value: coerceCellValueForApi(opt.cells?.[d.id], d.type),
      })),
    };
  }

  /**
   * Closes the modal, resets local UI state, and optionally refreshes workflow variables from the server.
   */
  const handleClose = () => {
    columnPopupAnchorRef.current = null;
    dimensionFilterPopupAnchorRef.current = null;
    setShowMultiDimensionChipModal(false);
    setColumnPopup(null);
    setDimensionFilterPopup(null);
    setColumnFilters({});
    setShowAddRowClearsFiltersDialog(false);
    optionPersistSnapshotRef.current = {};
    const isMultiDimensionDedicatedRoute = typeof window !== "undefined" && window.location.pathname.includes("workflowMultiDimension");
    if (!isMultiDimensionDedicatedRoute && workflow?.Id) {
      void (async () => {
        const list = await getWorkflowVariables({ workflowId: workflow.Id });
        if (Array.isArray(list)) {
          dispatch(setOriginalWorkflowVariables([...list]));
          dispatch(setWorkflowCustomVariables([...list]));
          setCustomWorkflowVariables([...list]);
          setSelectedVariable(list.find((v) => v.id === selectedVariable?.id));
        }
      })();
    }
    if (persistAdminGlobalMultiDimensionApis && adminMultiDimensionModalCloseCallbackRef?.current) {
      void Promise.resolve(adminMultiDimensionModalCloseCallbackRef.current()).catch(() => {});
    }
  };

  /**
   * Recomputes `optionPersistSnapshotRef` from the current matrix so blur-persist can skip no-op updates.
   */
  const refreshPersistedOptionSnapshots = (matrix) => {
    const next = {};
    if (!matrix?.options?.length) {
      optionPersistSnapshotRef.current = next;
      return;
    }
    const dims = matrix.dimensions;
    for (const o of matrix.options) {
      if (!isServerOptionRow(o.id)) continue;
      next[String(o.id)] = JSON.stringify(buildOptionApiPayload(o, dims));
    }
    optionPersistSnapshotRef.current = next;
  };

  const applyMatrixFromVariableResponse = (variable, options) => {
    const matrix = getMultiDimensionMatrixFromVariable(variable);
    if (!matrix) return;
    setDimensions(matrix.dimensions);
    setOptions(matrix.options);
    refreshPersistedOptionSnapshots(matrix);
    const onlyId = options?.clearOnlyColumnFilterId;
    if (onlyId != null && onlyId !== "") {
      setColumnFilters((prev) => {
        const next = { ...prev };
        delete next[onlyId];
        return next;
      });
    } else {
      setColumnFilters({});
    }
    setSelectedVariable(variable);
  };

  /**
   * Updates modal `dimensions` and `options` from a workflow variable without touching column filters or Redux context.
   */
  const applyModalGridFromVariable = (variable) => {
    const matrix = getMultiDimensionMatrixFromVariable(variable);
    if (!matrix) {
      setDimensions([]);
      setOptions([]);
      optionPersistSnapshotRef.current = {};
      return;
    }
    setDimensions(matrix.dimensions);
    setOptions(matrix.options);
    refreshPersistedOptionSnapshots(matrix);
  };

  /**
   * Fetches dimensions/options bundle, merges into `selectedVariable`, and refreshes the grid.
   */
  const syncMultiDimensionModalFromServer = async ({ workflowVariableId, workflowId, selectedVariable: variable, attrs, isCancelled }) => {
    const bundle = await getDimensionAndOptions({ workflowVariableId, workflowId });
    if (isCancelled()) return;

    const rawDims = bundle?.dimensions ?? attrs.dimensions ?? [];
    const rawOpts = bundle?.options ?? attrs.options ?? [];
    const mergedVariable = {
      ...variable,
      attributes: {
        ...attrs,
        dimensions: rawDims,
        options: rawOpts,
      },
    };

    applyModalGridFromVariable(mergedVariable);
    setSelectedVariable(mergedVariable);
  };

  const syncProjectMultiDimensionModalFromServer = async ({ projectVariableId, projectId, selectedVariable: variable, attrs, isCancelled }) => {
    const bundle = await getProjectVariableDimensionAndOptions({ projectVariableId, projectId });
    if (isCancelled()) return;

    const rawDims = bundle?.dimensions ?? attrs.dimensions ?? [];
    const rawOpts = bundle?.options ?? attrs.options ?? [];
    const mergedVariable = {
      ...variable,
      attributes: {
        ...attrs,
        dimensions: rawDims,
        options: rawOpts,
      },
    };

    applyModalGridFromVariable(mergedVariable);
    setSelectedVariable(mergedVariable);
  };

  /** Global / org admin custom variables (Profile Control): GET dimensions + options bundle. */
  const syncAdminGlobalMultiDimensionModalFromServer = async ({ variableId, companyId, selectedVariable: variable, attrs, isCancelled }) => {
    const bundle = await getAdminCustomVariableDimensionAndOptions({ variableId, companyId });
    if (isCancelled()) return;

    const rawDims = bundle?.dimensions ?? attrs.dimensions ?? [];
    const rawOpts = bundle?.options ?? attrs.options ?? [];
    const mergedVariable = {
      ...variable,
      attributes: {
        ...attrs,
        dimensions: rawDims,
        options: rawOpts,
      },
    };

    applyModalGridFromVariable(mergedVariable);
    setSelectedVariable(mergedVariable);
  };

  /**
   * Loads matrix data when the modal opens: uses in-memory variable if there is no workflow id or skip-fetch is set; otherwise fetches from API.
   */
  const runMultiDimensionModalOpenLoad = async ({
    workflowId,
    projectIdForDim,
    workflowVariableId,
    selectedVariable: variable,
    attrs,
    isCancelled,
  }) => {
    const useWorkflowFetch = Boolean(workflowId && !variable?.isProjectVariable);
    const useProjectFetch = Boolean(projectIdForDim && workflowVariableId);
    const useAdminGlobalFetch = Boolean(
      workflowVariableId &&
        variable?.companyId &&
        variable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD &&
        !useWorkflowFetch &&
        !useProjectFetch
    );

    if (!useWorkflowFetch && !useProjectFetch && !useAdminGlobalFetch) {
      if (!isCancelled()) applyModalGridFromVariable(variable);
      return;
    }
    const skipApiFetch = skipDimensionOptionsFetchOnModalOpenRef.current;
    skipDimensionOptionsFetchOnModalOpenRef.current = false;
    if (skipApiFetch) {
      if (!isCancelled()) applyModalGridFromVariable(variable);
      return;
    }
    if (useWorkflowFetch) {
      await syncMultiDimensionModalFromServer({
        workflowVariableId,
        workflowId,
        selectedVariable: variable,
        attrs,
        isCancelled,
      });
      return;
    }
    if (useProjectFetch) {
      await syncProjectMultiDimensionModalFromServer({
        projectVariableId: workflowVariableId,
        projectId: projectIdForDim,
        selectedVariable: variable,
        attrs,
        isCancelled,
      });
      return;
    }
    await syncAdminGlobalMultiDimensionModalFromServer({
      variableId: workflowVariableId,
      companyId: variable.companyId,
      selectedVariable: variable,
      attrs,
      isCancelled,
    });
  };

  /**
   * When the chip modal opens for a variable: clears column filters and loads grid state (in-memory, or API unless skip-fetch ref is set).
   */
  useEffect(() => {
    if (!showMultiDimensionChipModal || !selectedVariable?.id) return;
    setColumnFilters({});

    let cancelled = false;
    const isCancelled = () => cancelled;

    void runMultiDimensionModalOpenLoad({
      workflowId: workflow?.Id,
      projectIdForDim: effectiveProjectIdForMultiDim,
      workflowVariableId: selectedVariable.id,
      selectedVariable,
      attrs: selectedVariable.attributes || {},
      isCancelled,
    });

    return () => {
      cancelled = true;
    };
  }, [showMultiDimensionChipModal, selectedVariable?.id, workflow?.Id, effectiveProjectIdForMultiDim, selectedVariable?.isProjectVariable]);

  /**
   * Resets `lastAddedRowId` after the next frame so row-highlight UX runs once per add.
   */
  useEffect(() => {
    if (!lastAddedRowId) return;
    const rafId = requestAnimationFrame(() => setLastAddedRowId(null));
    return () => cancelAnimationFrame(rafId);
  }, [lastAddedRowId]);

  /**
   * Closes the modal when the user clicks the backdrop (not inner content).
   * @param {React.MouseEvent} e
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  /**
   * Dismisses the per-dimension column filter popover and clears its anchor ref.
   */
  const closeDimensionFilterPopup = () => {
    dimensionFilterPopupAnchorRef.current = null;
    setDimensionFilterPopup(null);
  };

  /**
   * Opens the filter UI for a dimension column, anchored to the triggering control.
   * @param {{ id: string|number }} dim Dimension row metadata.
   * @param {React.SyntheticEvent} [event] Used for anchor element and stopPropagation.
   */
  const openColumnFilterForDimension = (dim, event) => {
    event?.stopPropagation?.();
    closeColumnPopup();
    dimensionFilterPopupAnchorRef.current = event?.currentTarget ?? null;
    setDimensionFilterPopup({ dimensionId: dim.id });
  };

  /**
   * Stores the composed filter state for one dimension and closes the filter popup.
   * @param {string|number} dimensionId
   * @param {Object} composite Filter model produced by the filter popup.
   */
  const applyColumnFilter = (dimensionId, composite) => {
    setColumnFilters((prev) => ({ ...prev, [dimensionId]: composite }));
    closeDimensionFilterPopup();
  };

  /**
   * Removes the filter for one dimension and closes the filter popup.
   * @param {string|number} dimensionId
   */
  const clearColumnFilter = (dimensionId) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[dimensionId];
      return next;
    });
    closeDimensionFilterPopup();
  };

  /**
   * Opens the “add dimension column” popup, guarded by max column count.
   * @param {React.SyntheticEvent} [event] Anchor for the popup.
   */
  const openAddColumnPopup = (event) => {
    closeDimensionFilterPopup();
    if (dimensions.length >= MULTI_DIMENSION_MAX_DIMENSION_COLUMNS) {
      getNotification(WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_MAX_COLUMNS_REACHED, notifyIcon.WARNING_ICON);
      return;
    }
    columnPopupAnchorRef.current = event?.currentTarget ?? null;
    setPopupName("");
    setPopupType(MULTI_DIMENSION_DEFAULT_TYPE);
    setColumnPopup({ mode: "add" });
  };

  /**
   * Opens the “edit dimension column” popup with the dimension’s current name and type.
   */
  const openEditColumnPopup = (dim, event) => {
    closeDimensionFilterPopup();
    columnPopupAnchorRef.current = event?.currentTarget ?? null;
    setPopupName(
      String(dim.name ?? "")
        .trim()
        .slice(0, NAME_MAX)
    );
    setPopupType(normalizeMultiDimensionType(dim.type));
    setColumnPopup({
      mode: "edit",
      dimensionId: dim.id,
    });
  };

  /**
   * Closes the add/edit column popup and clears its anchor ref.
   */
  const closeColumnPopup = () => {
    columnPopupAnchorRef.current = null;
    setColumnPopup(null);
  };

  /**
   * User cancelled the add/edit column popup.
   */
  const handleColumnPopupCancel = () => {
    closeColumnPopup();
  };

  /**
   * Updates the column name field with max length enforcement.
   * @param {string} value
   */
  const handlePopupNameChange = (value) => {
    setPopupName(value.slice(0, NAME_MAX));
  };

  /**
   * Updates the column type from the type dropdown.
   * @param {{ value?: { value?: string } }} e PrimeReact-style change event.
   */
  const handlePopupTypeChange = (e) => {
    setPopupType(e.value?.value ?? MULTI_DIMENSION_DEFAULT_TYPE);
  };

  /**
   * Whether another option row already uses this label (case-insensitive), excluding the given row.
   */
  const optionLabelTaken = (optionId, label) => {
    const t = String(label ?? "")
      .trim()
      .toLowerCase();
    if (!t) return false;
    return optionsRef.current.some(
      (o) =>
        String(o.id) !== String(optionId) &&
        String(o.label ?? "")
          .trim()
          .toLowerCase() === t
    );
  };

  /**
   * Persists an option row on blur when APIs are enabled, after duplicate-label validation.
   */
  const handleOptionPersistBlur = async (optionId) => {
    if (!persistMultiDimensionApis) return;
    const opt = optionsRef.current.find((o) => String(o.id) === String(optionId));
    if (!opt || !isServerOptionRow(opt.id)) return;
    if (optionLabelTaken(opt.id, opt.label)) {
      getNotification(WORKFLOW_VARIABLE_LABELS.NO_DUPLICATE_OPTIONS, notifyIcon.ERROR_ICON);
      return;
    }
    const serialized = JSON.stringify(buildOptionApiPayload(opt));
    const snapKey = String(optionId);
    if (optionPersistSnapshotRef.current[snapKey] === serialized) return;

    let result = null;
    if (persistWorkflowMultiDimensionApis) {
      result = await updateWorkflowVariableOption({
        workflowVariableId: selectedVariable.id,
        workflowId: workflow.Id,
        option: buildOptionApiPayload(opt),
      });
    } else if (persistProjectMultiDimensionApis) {
      result = await updateProjectVariableOption({
        projectVariableId: selectedVariable.id,
        projectId: effectiveProjectIdForMultiDim,
        option: buildOptionApiPayload(opt),
      });
    } else if (persistAdminGlobalMultiDimensionApis) {
      result = await updateAdminCustomVariableOption({
        variableId: selectedVariable.id,
        companyId: selectedVariable.companyId,
        option: buildOptionApiPayload(opt),
      });
    }
    if (result == null) return;
    applyMatrixFromVariableResponse(result);
  };

  /** Persist only when focus leaves the row (not when tabbing between label and cells in the same row). */
  const handleOptionRowBlur = (optionId, event) => {
    const related = event.relatedTarget;
    if (related && typeof related === "object" && event.currentTarget.contains(related)) return;
    void handleOptionPersistBlur(optionId);
  };

  /**
   * Validates add-column input; when persistence is enabled, creates the dimension via API and syncs the matrix; always closes the popup.
   */
  const handlePopupSaveAdd = async () => {
    const name = popupName.trim().slice(0, NAME_MAX);
    if (!name) {
      getNotification(WORKFLOW_VARIABLE_LABELS.NAME_REQUIRED, notifyIcon.WARNING_ICON);
      return;
    }
    if (dimensions.length >= MULTI_DIMENSION_MAX_DIMENSION_COLUMNS) {
      getNotification(WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_MAX_COLUMNS_REACHED, notifyIcon.WARNING_ICON);
      return;
    }

    const newDimensionNumericId = dimensions.length + 1;
    const typeNorm = normalizeMultiDimensionType(popupType);

    if (persistMultiDimensionApis) {
      let result = null;
      if (persistWorkflowMultiDimensionApis) {
        result = await addWorkflowVariableDimension({
          workflowVariableId: selectedVariable.id,
          workflowId: workflow.Id,
          dimension: { id: newDimensionNumericId, label: name, type: typeNorm },
        });
      } else if (persistProjectMultiDimensionApis) {
        result = await addProjectVariableDimension({
          projectVariableId: selectedVariable.id,
          projectId: effectiveProjectIdForMultiDim,
          dimension: { id: newDimensionNumericId, label: name, type: typeNorm },
        });
      } else if (persistAdminGlobalMultiDimensionApis) {
        result = await addAdminCustomVariableDimension({
          variableId: selectedVariable.id,
          companyId: selectedVariable.companyId,
          dimension: { id: newDimensionNumericId, label: name, type: typeNorm },
        });
      }
      if (result == null) return;
      applyMatrixFromVariableResponse(result);
    }
    closeColumnPopup();
  };

  /**
   * When persistence is enabled, updates the column via API and may clear that column's filter; always closes the popup.
   */
  const handlePopupUpdateColumn = async () => {
    if (!columnPopup || columnPopup.mode !== "edit") return;
    const name = popupName.trim().slice(0, NAME_MAX);
    if (!name) {
      getNotification(WORKFLOW_VARIABLE_LABELS.NAME_REQUIRED, notifyIcon.WARNING_ICON);
      return;
    }
    const { dimensionId } = columnPopup;
    const typeNorm = normalizeMultiDimensionType(popupType);
    const dimNumericId = Number(dimensionId);

    if (persistMultiDimensionApis) {
      let result = null;
      if (persistWorkflowMultiDimensionApis) {
        result = await updateWorkflowVariableDimension({
          workflowVariableId: selectedVariable.id,
          workflowId: workflow.Id,
          dimension: {
            id: Number.isFinite(dimNumericId) ? dimNumericId : dimensionId,
            label: name,
            type: typeNorm,
          },
        });
      } else if (persistProjectMultiDimensionApis) {
        result = await updateProjectVariableDimension({
          projectVariableId: selectedVariable.id,
          projectId: effectiveProjectIdForMultiDim,
          dimension: {
            id: Number.isFinite(dimNumericId) ? dimNumericId : dimensionId,
            label: name,
            type: typeNorm,
          },
        });
      } else if (persistAdminGlobalMultiDimensionApis) {
        result = await updateAdminCustomVariableDimension({
          variableId: selectedVariable.id,
          companyId: selectedVariable.companyId,
          dimension: {
            id: Number.isFinite(dimNumericId) ? dimNumericId : dimensionId,
            label: name,
            type: typeNorm,
          },
        });
      }
      if (result == null) return;
      applyMatrixFromVariableResponse(result, { clearOnlyColumnFilterId: dimensionId });
    }
    closeColumnPopup();
  };

  /**
   * When persistence is enabled, deletes the edited column via API (guarded by minimum column count); always closes the popup.
   */
  const handlePopupDeleteColumn = async () => {
    if (!columnPopup || columnPopup.mode !== "edit") return;
    if (dimensions.length <= MULTI_DIMENSION_MIN_DIMENSION_COLUMNS) {
      getNotification(WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_MIN_COLUMNS, notifyIcon.WARNING_ICON);
      return;
    }
    const { dimensionId } = columnPopup;
    const dimNumericId = Number(dimensionId);

    if (persistMultiDimensionApis) {
      let result = null;
      if (persistWorkflowMultiDimensionApis) {
        result = await deleteWorkflowVariableDimension({
          workflowVariableId: selectedVariable.id,
          workflowId: workflow.Id,
          id: Number.isFinite(dimNumericId) ? dimNumericId : dimensionId,
        });
      } else if (persistProjectMultiDimensionApis) {
        result = await deleteProjectVariableDimension({
          projectVariableId: selectedVariable.id,
          projectId: effectiveProjectIdForMultiDim,
          id: Number.isFinite(dimNumericId) ? dimNumericId : dimensionId,
        });
      } else if (persistAdminGlobalMultiDimensionApis) {
        result = await deleteAdminCustomVariableDimension({
          variableId: selectedVariable.id,
          companyId: selectedVariable.companyId,
          id: Number.isFinite(dimNumericId) ? dimNumericId : dimensionId,
        });
      }
      if (result == null) return;
      applyMatrixFromVariableResponse(result);
    }
    closeColumnPopup();
  };

  /**
   * Appends a new option row (draft locally or persisted via API) and records `lastAddedRowId` for scroll/highlight UX.
   */
  const appendOptionRow = async () => {
    if (!dimensions.length) return;
    const numberOnly = !!selectedVariable?.attributes?.numberOnly;
    if (!persistMultiDimensionApis) {
      const newId = `opt_${Date.now()}`;
      setOptions((prev) => {
        const numericLabels = prev.map((o) => Number(String(o.label ?? "").trim())).filter((n) => Number.isFinite(n));
        const nextNum = numberOnly ? (numericLabels.length ? Math.max(...numericLabels) : 0) + 1 : prev.length + 1;
        const cells = {};
        dimensions.forEach((d) => {
          cells[d.id] = "";
        });
        const row = { id: newId, label: numberOnly ? String(nextNum) : `Option ${nextNum}`, cells };
        if (numberOnly) row.number = nextNum;
        return [...prev, row];
      });
      setLastAddedRowId(newId);
      return;
    }
    if (optionAddInFlightRef.current) return;
    optionAddInFlightRef.current = true;
    setIsAddingOptionRow(true);
    try {
      const opts = optionsRef.current;
      let nextValue = opts.length ? Math.max(0, ...opts.map((o) => Number(o.id) || 0)) + 1 : 1;
      let label = numberOnly ? String(nextValue) : `Option ${nextValue}`;
      while (
        opts.some(
          (o) =>
            String(o.label ?? "")
              .trim()
              .toLowerCase() === label.trim().toLowerCase()
        )
      ) {
        nextValue += 1;
        label = numberOnly ? String(nextValue) : `Option ${nextValue}`;
      }

      const cellsPayload = dimensionsRef.current.map((d) => ({ dimensionId: d.id, value: "" }));
      const option = { value: nextValue, label, cells: cellsPayload };
      if (numberOnly) option.number = nextValue;
      let result = null;
      if (persistWorkflowMultiDimensionApis) {
        result = await addWorkflowVariableOption({
          workflowVariableId: selectedVariable.id,
          workflowId: workflow.Id,
          option,
        });
      } else if (persistProjectMultiDimensionApis) {
        result = await addProjectVariableOption({
          projectVariableId: selectedVariable.id,
          projectId: effectiveProjectIdForMultiDim,
          option,
        });
      } else if (persistAdminGlobalMultiDimensionApis) {
        result = await addAdminCustomVariableOption({
          variableId: selectedVariable.id,
          companyId: selectedVariable.companyId,
          option,
        });
      }
      if (result == null) return;
      applyMatrixFromVariableResponse(result);
      setLastAddedRowId(nextValue);
    } finally {
      optionAddInFlightRef.current = false;
      setIsAddingOptionRow(false);
    }
  };

  /**
   * User requested a new row; may open a confirmation if column filters are active.
   */
  const handleAddRow = () => {
    if (hasActiveColumnFilters) {
      setShowAddRowClearsFiltersDialog(true);
      return;
    }
    void appendOptionRow();
  };

  /**
   * Closes the “add row clears filters” dialog without adding a row.
   */
  const dismissAddRowClearFiltersDialog = () => {
    setShowAddRowClearsFiltersDialog(false);
  };

  /**
   * Clears all column filters and proceeds to append a row.
   */
  const confirmAddRowAfterClearFilters = () => {
    setColumnFilters({});
    closeDimensionFilterPopup();
    setShowAddRowClearsFiltersDialog(false);
    void appendOptionRow();
  };

  /**
   * @param {string|number} optionId Row being dragged.
   */
  const handleDragStart = (optionId) => setDraggedOptionId(optionId);

  /**
   * Reorders option rows when dragging over a target row.
   */
  const handleDragOver = (e, targetOptionId) => {
    e.preventDefault();
    if (!draggedOptionId || draggedOptionId === targetOptionId) return;
    setOptions((prev) => {
      const from = prev.findIndex((o) => o.id === draggedOptionId);
      let to = prev.findIndex((o) => o.id === targetOptionId);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      if (from < to) to -= 1;
      next.splice(to, 0, item);
      return next;
    });
  };

  /**
   * Clears drag state after drop or cancel.
   */
  const handleDragEnd = () => setDraggedOptionId(null);

  /**
   * Updates the label for one option row in local state.
   */
  const handleOptionLabelChange = (optionId, value) => {
    setOptions((prev) => {
      const idx = prev.findIndex((o) => o.id === optionId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], label: value };
      return next;
    });
  };

  /**
   * Updates one matrix cell in local state.
   */
  const handleCellChange = (optionId, dimensionId, value) => {
    setOptions((prev) => {
      const idx = prev.findIndex((o) => o.id === optionId);
      if (idx < 0) return prev;
      const next = [...prev];
      const opt = next[idx];
      next[idx] = { ...opt, cells: { ...opt.cells, [dimensionId]: value } };
      return next;
    });
  };

  /**
   * Removes an option row locally or calls delete API for persisted rows.
   */
  const handleDeleteOptionRow = async (optionId) => {
    const opt = optionsRef.current.find((o) => String(o.id) === String(optionId));
    if (!opt) return;

    if (!persistMultiDimensionApis || !isServerOptionRow(opt.id)) {
      setOptions((prev) => prev.filter((o) => String(o.id) !== String(optionId)));
      return;
    }

    let result = null;
    if (persistWorkflowMultiDimensionApis) {
      result = await deleteWorkflowVariableOption({
        workflowVariableId: selectedVariable.id,
        value: opt.id,
        workflowId: workflow.Id,
      });
    } else if (persistProjectMultiDimensionApis) {
      result = await deleteProjectVariableOption({
        projectVariableId: selectedVariable.id,
        projectId: effectiveProjectIdForMultiDim,
        value: opt.id,
      });
    } else if (persistAdminGlobalMultiDimensionApis) {
      result = await deleteAdminCustomVariableOption({
        variableId: selectedVariable.id,
        companyId: selectedVariable.companyId,
        value: opt.id,
      });
    }
    if (result == null) return;
    applyMatrixFromVariableResponse(result);
  };

  const handleDeleteAllOptionRows = async () => {
    if (!persistMultiDimensionApis) {
      setOptions([]);
      return;
    }
    let result = null;
    if (persistWorkflowMultiDimensionApis) {
      result = await deleteAllOptions({
        workflowVariableId: selectedVariable.id,
        workflowId: workflow.Id,
      });
    } else if (persistProjectMultiDimensionApis) {
      result = await deleteAllProjectVariableOptions({
        projectVariableId: selectedVariable.id,
        projectId: effectiveProjectIdForMultiDim,
      });
    } else if (persistAdminGlobalMultiDimensionApis) {
      result = await deleteAllAdminCustomVariableOptions({
        variableId: selectedVariable.id,
        companyId: selectedVariable.companyId,
      });
    }
    if (result == null) return;
    applyMatrixFromVariableResponse(result);
  };

  /**
   * Resolves the icon component key for a dimension type.
   */
  const getDimensionIcon = (type) => {
    const t = normalizeMultiDimensionType(type);
    return VARIABLE_ICONS[t] || VARIABLE_ICONS.text;
  };

  const optionsColumnFilterDimension = useMemo(
    () => ({
      id: MULTI_DIMENSION_OPTIONS_COLUMN_FILTER_ID,
      name: WORKFLOW_VARIABLE_LABELS.OPTIONS_COLUMN_LABEL,
      type: selectedVariable?.attributes?.numberOnly ? "number" : "text",
    }),
    [selectedVariable?.attributes?.numberOnly]
  );

  const atMaxColumns = dimensions.length >= MULTI_DIMENSION_MAX_DIMENSION_COLUMNS;
  const saveDisabledAdd = !popupName.trim();
  const saveDisabledUpdate = useMemo(() => {
    if (!columnPopup || columnPopup.mode !== "edit") return false;
    const dim = dimensions.find((d) => String(d.id) === String(columnPopup.dimensionId));
    if (!dim) return true;
    const name = String(popupName ?? "")
      .trim()
      .slice(0, NAME_MAX);
    if (!name) return true;
    const originalName = String(dim.name ?? "")
      .trim()
      .slice(0, NAME_MAX);
    const unchanged = name === originalName && normalizeMultiDimensionType(popupType) === normalizeMultiDimensionType(dim.type);
    return unchanged;
  }, [columnPopup, dimensions, popupName, popupType]);

  const isOpen = showMultiDimensionChipModal && !!selectedVariable;

  const displayedOptions = useMemo(
    () => filterMultiDimensionOptions(options, dimensions, columnFilters, selectedVariable?.attributes?.numberOnly ? "number" : "text"),
    [options, dimensions, columnFilters, selectedVariable?.attributes?.numberOnly]
  );

  return {
    isOpen,
    selectedVariable,
    dimensions,
    options,
    displayedOptions,
    columnFilters,
    dimensionFilterPopup,
    dimensionFilterPopupAnchorRef,
    lastAddedRowId,
    columnPopup,
    popupName,
    popupType,
    nameMax: NAME_MAX,
    atMaxColumns,
    saveDisabledAdd,
    saveDisabledUpdate,
    isAddingOptionRow,
    handleClose,
    handleBackdropClick,
    handleAddRow,
    showAddRowClearsFiltersDialog,
    dismissAddRowClearFiltersDialog,
    confirmAddRowAfterClearFilters,
    openAddColumnPopup,
    openEditColumnPopup,
    openColumnFilterForDimension,
    optionsColumnFilterDimension,
    applyColumnFilter,
    clearColumnFilter,
    dismissColumnFilterPopup: closeDimensionFilterPopup,
    dismissColumnPopup: closeColumnPopup,
    handleColumnPopupCancel,
    handlePopupNameChange,
    handlePopupTypeChange,
    handlePopupSaveAdd,
    handlePopupUpdateColumn,
    handlePopupDeleteColumn,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleOptionLabelChange,
    handleCellChange,
    handleDeleteOptionRow,
    handleDeleteAllOptionRows,
    handleOptionPersistBlur,
    handleOptionRowBlur,
    getDimensionIcon,
    columnPopupAnchorRef,
  };
};
